import React, { useState, useEffect } from 'react'
import Public from '@mui/icons-material/Public'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import CheckIcon from '@mui/icons-material/Check'
import ReactCountryFlag from "react-country-flag"
import { useAuth } from "./api/authStore"
import RenewProxyConfirmDialog from './RenewProxyConfirmDialog'
import RefundProxyConfirmDialog from './RefundProxyConfirmDialog'
import moment from 'moment'
import useMobile from './hooks/useMobile'
import LoadingSpinner from './components/LoadingSpinner'

const CONN_FORMAT_STORAGE_KEY = 'xsocks_conn_format'
const VALID_CONN_FORMATS = ['uri', 'hostPortUserPass', 'userPassHostPort']

const getSavedConnFormat = () => {
    try {
        const saved = window.localStorage.getItem(CONN_FORMAT_STORAGE_KEY)
        if (saved && VALID_CONN_FORMATS.includes(saved)) return saved
    } catch {
        /* ignore */
    }
    return 'uri'
}

// Компонент тултипа (мобильно-дружественный)
const Tooltip = ({ children, title, onLongPress, isCheckingSpeed }) => {
    const [show, setShow] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [touchTimer, setTouchTimer] = useState(null)
    
    // Определяем, мобильное ли устройство
    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window)
        }
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])
    
    const handleTouchStart = (e) => {
        if (isMobile) {
            // Короткое касание - показываем тултип
            setShow(true)
            setTimeout(() => setShow(false), 3000)
            
            // Длительное зажатие - запускаем проверку скорости
            const timer = setTimeout(() => {
                if (onLongPress) {
                    console.log('Long press triggered in ProxyDetailsDialog!') // Отладка
                    onLongPress(e)
                }
            }, 600) // 600ms для длительного нажатия
            
            setTouchTimer(timer)
        }
    }
    
    const handleTouchEnd = (e) => {
        if (isMobile && touchTimer) {
            // Если отпустили до истечения 600ms, отменяем длительное нажатие
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    const handleTouchCancel = (e) => {
        if (isMobile && touchTimer) {
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    return (
        <div 
            style={{ 
                position: 'relative', 
                display: 'inline-block',
                width: isMobile ? '100%' : 'auto'
            }}
            onMouseEnter={() => !isMobile && setShow(true)}
            onMouseLeave={() => !isMobile && setShow(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
        >
            {/* Показываем спиннер только если идет проверка скорости */}
            {isCheckingSpeed ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <LoadingSpinner size={22} />
                </div>
            ) : (
                children
            )}
            {show && !isCheckingSpeed && (
                <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'var(--medium-gray)',
                    color: 'var(--text-light)',
                    padding: '8px 10px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-xs)',
                    whiteSpace: 'nowrap',
                    zIndex: 1000,
                    border: '1px solid var(--light-gray)',
                    boxShadow: 'var(--shadow-md)',
                    marginBottom: '5px'
                }}>
                    {title}
                </div>
            )}
        </div>
    )
}

// Компонент для уведомлений
const CustomSnackbar = ({ open, onClose, message, severity = 'success' }) => {
    if (!open) return null

    const getSeverityMeta = (severity) => {
        switch (severity) {
            case 'error':
                return { accent: '#E5484D', icon: '✕' }
            case 'success':
                return { accent: '#2EA043', icon: '✓' }
            default:
                return { accent: 'var(--primary-orange)', icon: '!' }
        }
    }

    const { accent, icon } = getSeverityMeta(severity)

    return (
        <div style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10001,
            background: 'var(--medium-gray)',
            color: 'var(--text-light)',
            border: '1px solid var(--light-gray)',
            borderLeft: `4px solid ${accent}`,
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-md) var(--spacing-lg)',
            boxShadow: 'var(--shadow-card)',
            minWidth: '300px',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-md)',
            animation: 'slideDown 0.3s ease-out'
        }}>
            <span style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: accent,
                color: '#fff',
                fontSize: '14px',
                fontWeight: '700',
                flexShrink: 0
            }}>
                {icon}
            </span>
            <span style={{ flex: 1, fontSize: 'var(--text-sm)', fontWeight: '500' }}>
                {message}
            </span>
            <button
                onClick={onClose}
                style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    fontSize: 'var(--text-lg)',
                    cursor: 'pointer',
                    padding: '0',
                    lineHeight: '1',
                    flexShrink: 0
                }}
            >
                ✕
            </button>
            <style>{`
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateX(-50%) translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(-50%) translateY(0);
                    }
                }
            `}</style>
        </div>
    )
}

const ProxyDetailsDialog = (props) => {
    const { user, getAccessTokenSilently, setToken, logout } = useAuth()
    const { isMobile } = useMobile()

    const selectedProxy = props.selectedProxy
    const [saveWhitelistError, setSaveWhitelistError] = useState(false)
    const [whitelist, setWhitelist] = useState(null)
    const [buttonStatus, setButtonDisbled] = useState(true)
    const [isSettingMyIp, setIsSettingMyIp] = useState(false)
    const [connFormat, setConnFormatState] = useState(() => getSavedConnFormat())
    const [connCopied, setConnCopied] = useState(false)

    const setConnFormat = (format) => {
        setConnFormatState(format)
        try {
            window.localStorage.setItem(CONN_FORMAT_STORAGE_KEY, format)
        } catch {
            /* ignore */
        }
    }
    const [openSuccessWhitelistUpdate, setOpenSuccessWhitelistUpdate] = useState(false)
    const [openConfirmRenew, setOpenConfirmRenew] = useState(false)
    const [openConfirmRefund, setOpenConfirmRefund] = useState(false)
    const [renewError, setRenewError] = useState(false)
    const [renewSuccess, setRenewSuccess] = useState(false)
    const [refundError, setRefundError] = useState(false)
    const [refundSuccess, setRefundSuccess] = useState(false)
    const [duplicateTicketError, setDuplicateTicketError] = useState(false)
    
    // Состояние для проверки скорости прокси (как в Proxies.js)
    const [isCheckingSpeed, setIsCheckingSpeed] = useState(false)
    const [isLoadingSpeed, setIsLoadingSpeed] = useState(false)
    const [proxySpeed, setProxySpeed] = useState(null)
    const [speedCheckTime, setSpeedCheckTime] = useState(null)
    
    // CSS стили для анимации спиннера
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])

    // Автоматическое закрытие уведомлений через 4 секунды
    useEffect(() => {
        if (renewSuccess) {
            const timer = setTimeout(() => {
                setRenewSuccess(false)
            }, 4000)
            
            return () => clearTimeout(timer)
        }
    }, [renewSuccess])

    useEffect(() => {
        if (refundSuccess) {
            const timer = setTimeout(() => {
                setRefundSuccess(false)
            }, 4000)
            
            return () => clearTimeout(timer)
        }
    }, [refundSuccess])

    useEffect(() => {
        if (duplicateTicketError) {
            const timer = setTimeout(() => {
                setDuplicateTicketError(false)
            }, 4000)
            
            return () => clearTimeout(timer)
        }
    }, [duplicateTicketError])

    useEffect(() => {
        if (openSuccessWhitelistUpdate) {
            const timer = setTimeout(() => {
                setOpenSuccessWhitelistUpdate(false)
            }, 4000)
            
            return () => clearTimeout(timer)
        }
    }, [openSuccessWhitelistUpdate])

    useEffect(() => {
        if (saveWhitelistError) {
            const timer = setTimeout(() => {
                setSaveWhitelistError(false)
            }, 4000)

            return () => clearTimeout(timer)
        }
    }, [saveWhitelistError])

    useEffect(() => {
        if (renewError) {
            const timer = setTimeout(() => {
                setRenewError(false)
            }, 4000)

            return () => clearTimeout(timer)
        }
    }, [renewError])

    useEffect(() => {
        if (refundError) {
            const timer = setTimeout(() => {
                setRefundError(false)
            }, 4000)

            return () => clearTimeout(timer)
        }
    }, [refundError])

    // Вычисляем время до истечения
    const currentTime = new Date().getTime()
    const endTime = new Date(selectedProxy?.expiresAt).getTime()
    const hoursUntilExpiry = Math.max(0, Math.ceil((endTime - currentTime) / (1000 * 60 * 60)))
    
    // Вычисляем время с момента покупки для refund (60 минут = 1 час)
    const startTime = new Date(selectedProxy?.startedAt).getTime()
    const hoursSincePurchase = Math.floor((currentTime - startTime) / (1000 * 60 * 60))
    const minutesSincePurchase = Math.floor((currentTime - startTime) / (1000 * 60))
    const canRefund = minutesSincePurchase <= 60 // 60 минут после покупки


    // Загрузка данных прокси при открытии диалога
    useEffect(() => {
        const loadProxyData = async () => {
            if (props.open && selectedProxy?.proxyid) {
                try {
                    setIsLoadingSpeed(true)

                    // Загружаем полную информацию о прокси по proxyid
                    const proxyData = await props.apiClient.setToken(getAccessTokenSilently()).getAgentInfo(selectedProxy.proxyid)
                    
                    // Если есть данные о скорости, используем их
                    if (proxyData?.speed?.bytesPerSec != null) {
                        const speed = (proxyData.speed.bytesPerSec / 1000 / 1000).toFixed(2)
                        setProxySpeed(speed)
                        setSpeedCheckTime(proxyData.speed.checkedAt)
                    } else {
                        // Если нет данных о скорости, устанавливаем null
                        setProxySpeed(null)
                    }
                } catch (e) {
                    setProxySpeed(null)
                } finally {
                    setIsLoadingSpeed(false)
                }
            }
        }
        
        loadProxyData()
    }, [props.open, selectedProxy?.proxyid])

    // Инициализация поля whitelist при открытии диалога
    useEffect(() => {
        if (props.open) {
            setWhitelist(selectedProxy?.ipAuth?.whitelist || '')
        }
    }, [props.open, selectedProxy?.rentalid])

    const handleClose = () => {
        props.setOpenDetails(false)
    }

    // Закрытие диалога по нажатию Esc
    useEffect(() => {
        if (!props.open) return

        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                props.setOpenDetails(false)
            }
        }

        window.addEventListener('keydown', handleEsc)
        return () => window.removeEventListener('keydown', handleEsc)
    }, [props.open])

    const handleSetSaveErrorClose = () => {
        setSaveWhitelistError(false)
    }

    const handleSetOpenSuccessWhitelistUpdate = () => {
        setOpenSuccessWhitelistUpdate(false)
    }

    const handleSetRenewErrorClose = () => {
        setRenewError(false)
    }

    const handleSetRenewSuccessClose = () => {
        setRenewSuccess(false)
    }

    const handleSetRefundSuccessClose = () => {
        setRefundSuccess(false)
    }

    const handleSetRefundErrorClose = () => {
        setRefundError(false)
    }

    const handleSetDuplicateTicketErrorClose = () => {
        setDuplicateTicketError(false)
    }

    // Функция проверки скорости прокси
    const checkSpeed = async () => {
        try {
            setIsCheckingSpeed(true)

            const response = await props.apiClient.setToken(getAccessTokenSilently()).checkProxySpeed(selectedProxy?.proxyid)
            const speed = (response?.bytesPerSec != null) ? (response.bytesPerSec / 1000 / 1000).toFixed(2) : null
            
            setProxySpeed(speed)
            setSpeedCheckTime(response?.checkedAt)
        } catch (e) {
            // В случае ошибки показываем "Error"
            setProxySpeed('Error')
        } finally {
            setIsCheckingSpeed(false)
        }
    }

    const handleWhiteListChange = (e) => {
        const regexExpIP = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/gi

        if (e.target.value == "") {
            setButtonDisbled(true)
            setWhitelist(e.target.value)
            return
        }

        const ipsArr = e.target.value.split(',')
        let checkedCount = 0

        ipsArr.forEach(element => {
            if (!element.match(regexExpIP)) {
                setButtonDisbled(true)
            } else {
                checkedCount++
            }
        })

        if (checkedCount == ipsArr.length) {
            setButtonDisbled(false)
        }

        setWhitelist(e.target.value)
    }

    const handleWhitelistSave = async () => {
        try {
            const response = await props.apiClient.setToken(getAccessTokenSilently()).saveProxyWhitelist(selectedProxy.rentalid, whitelist)

            setOpenSuccessWhitelistUpdate(true)
            props.setReloadActive(true)
        } catch (e) {
            setSaveWhitelistError(true)
        }
    }

    // Запрос на запись текущего IP клиента в whitelist (IP определяет бэкенд)
    const handleUseMyIp = async () => {
        try {
            setIsSettingMyIp(true)

            const response = await props.apiClient.setToken(getAccessTokenSilently()).saveProxyWhitelist(selectedProxy.rentalid, 'userIP')

            // Берём IP, который вернул сервер
            const newWhitelist = response?.ipAuth?.whitelist || ''
            setWhitelist(newWhitelist)
            setButtonDisbled(true)
            setOpenSuccessWhitelistUpdate(true)
            props.setReloadActive(true)
        } catch (e) {
            setSaveWhitelistError(true)
        } finally {
            setIsSettingMyIp(false)
        }
    }

    const handleRenewClick = async () => {
        try {
            setOpenConfirmRenew(true)
            props.setOpenDetails(false) // Закрываем основной диалог
        } catch (e) {
        }
    }

    const handleRefundClick = async () => {
        try {
            setOpenConfirmRefund(true)
            props.setOpenDetails(false) // Закрываем основной диалог
        } catch (e) {
        }
    }

    const renewProxy = async () => {
        try {
            const response = await props.apiClient.setToken(getAccessTokenSilently()).renewProxy(selectedProxy.rentalid)

            setRenewSuccess(true)
            props.refreshUserData?.()
            props.setReloadActive(true)
            props.setOpenDetails(false)
        } catch (e) {
            setRenewError(true)
        }
    }

    const refundProxy = async (reason) => {
        try {
            const response = await props.apiClient.setToken(getAccessTokenSilently()).createRefundTicket(selectedProxy.rentalid, reason)

            setOpenConfirmRefund(false)
            setRefundSuccess(true)
            props.setOpenDetails(false)
        } catch (e) {
            setOpenConfirmRefund(false)
            
            // Проверяем, является ли это ошибкой дублирующего тикета
            if (e.response && e.response.status === 401) {
                // Это дублирующий тикет - показываем специальное сообщение
                setDuplicateTicketError(true)
            } else {
                setRefundError(true)
            }
        }
    }

    // Получаем полный IP адрес из proxyinfo
    const getFullIPAddress = () => {
        return selectedProxy?.proxyinfo?.ip || 'N/A'
    }

    // Доступные форматы строки подключения
    const connFormats = [
        { id: 'uri', label: 'URI' },
        { id: 'hostPortUserPass', label: 'host:port:user:pass' },
        { id: 'userPassHostPort', label: 'user:pass@host:port' },
    ]

    // Сборка строки подключения в выбранном формате
    const buildConnectionString = () => {
        const ip = selectedProxy?.socksAuth?.ip || ''
        const port = selectedProxy?.socksAuth?.port || ''
        const user = selectedProxy?.socksAuth?.user || ''
        const pass = selectedProxy?.socksAuth?.password || ''

        switch (connFormat) {
            case 'hostPortUserPass':
                return `${ip}:${port}:${user}:${pass}`
            case 'userPassHostPort':
                return `${user}:${pass}@${ip}:${port}`
            case 'uri':
            default:
                return `socks5h://${user}:${pass}@${ip}:${port}`
        }
    }

    const handleCopyConnectionString = async () => {
        try {
            await navigator.clipboard.writeText(buildConnectionString())
            setConnCopied(true)
            setTimeout(() => setConnCopied(false), 1500)
        } catch (e) {
            /* ignore */
        }
    }


    return (
        <>
            {/* Модальное окно */}
            {props.open && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                }} onClick={handleClose}>
                    <div style={{
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--spacing-2xl)',
                        maxWidth: '500px',
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: 'var(--shadow-card)',
                        position: 'relative'
                    }} onClick={(e) => e.stopPropagation()}>
                        
                        {/* Кнопка закрытия */}
                        <button
                            onClick={handleClose}
                            style={{
                                position: 'absolute',
                                top: 'var(--spacing-md)',
                                right: 'var(--spacing-md)',
                                background: 'none',
                                border: 'none',
                                color: 'var(--text-muted)',
                                fontSize: 'var(--text-xl)',
                                cursor: 'pointer',
                                padding: '4px'
                            }}
                        >
                            ✕
                        </button>

                        {/* Заголовок с флагом и информацией */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-xl)',
                            paddingTop: 'var(--spacing-lg)'
                        }}>
                            <div style={{
                                width: '40px',
                                height: '30px',
                                borderRadius: 'var(--radius-sm)',
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <ReactCountryFlag 
                                    countryCode={selectedProxy?.proxyinfo?.geo?.cc} 
                                    svg 
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </div>
                            <div>
                                <h2 style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-xl)',
                                    fontWeight: '700',
                                    margin: '0 0 var(--spacing-xs) 0'
                                }}>
                                    <span style={{ color: 'var(--primary-orange)' }}>Proxy ID:</span> {selectedProxy?.rentalid}
                                </h2>
                                <p style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    margin: '0',
                                    lineHeight: '1.4'
                                }}>
                                    {selectedProxy?.proxyinfo?.geo?.reg} • {selectedProxy?.proxyinfo?.geo?.city} • {selectedProxy?.proxyinfo?.geo?.zip} • {selectedProxy?.proxyinfo?.geo?.isp}
                                </p>
                            </div>
                        </div>

                        {/* IP адрес прокси */}
                        <div style={{
                            background: 'var(--dark-gray)',
                            border: '1px solid var(--light-gray)',
                            borderRadius: 'var(--radius-md)',
                            padding: 'var(--spacing-lg)',
                            marginBottom: 'var(--spacing-lg)'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: isMobile ? 'flex-start' : 'center',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: isMobile ? 'var(--spacing-xs)' : 'var(--spacing-md)',
                                marginBottom: 'var(--spacing-sm)'
                            }}>
                                <h3 style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    color: 'var(--primary-orange)',
                                    fontSize: 'var(--text-lg)',
                                    fontWeight: '600',
                                    margin: '0',
                                    whiteSpace: 'nowrap'
                                }}>
                                    <Public sx={{ fontSize: 22, color: 'var(--primary-orange)' }} />
                                    Proxy IP
                                </h3>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-base)',
                                    fontFamily: 'var(--font-mono)',
                                    fontWeight: '600'
                                }}>
                                    {getFullIPAddress()}
                                </span>
                            </div>
                            <p style={{
                                color: 'var(--text-muted)',
                                fontSize: 'var(--text-sm)',
                                lineHeight: '1.5',
                                margin: '0'
                            }}>
                                This proxy will be rented by you for 24 hours from the time of purchase.
                            </p>
                        </div>

                        {/* Авторизация по username */}
                        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                            <h3 style={{
                                color: 'var(--primary-orange)',
                                fontSize: 'var(--text-base)',
                                fontWeight: '600',
                                marginBottom: 'var(--spacing-md)'
                            }}>
                                Authorization by Username/Password
                            </h3>
                            
                            <div style={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: 'var(--spacing-md)',
                                marginBottom: 'var(--spacing-md)'
                            }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{
                                        color: 'var(--text-muted)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0 0 var(--spacing-xs) 0'
                                    }}>
                                        Socks5 Server:Port
                                    </p>
                                    <p style={{
                                        color: 'var(--text-light)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0',
                                        fontFamily: 'var(--font-mono)',
                                        wordBreak: 'break-all'
                                    }}>
                                        {selectedProxy?.socksAuth?.ip}:{selectedProxy?.socksAuth?.port}
                                    </p>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{
                                        color: 'var(--text-muted)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0 0 var(--spacing-xs) 0'
                                    }}>
                                        Username:Password
                                    </p>
                                    <p style={{
                                        color: 'var(--text-light)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0',
                                        fontFamily: 'var(--font-mono)',
                                        wordBreak: 'break-all'
                                    }}>
                                        {selectedProxy?.socksAuth?.user}:{selectedProxy?.socksAuth?.password}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Авторизация по IP */}
                        <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                            <h3 style={{
                                color: 'var(--primary-orange)',
                                fontSize: 'var(--text-base)',
                                fontWeight: '600',
                                marginBottom: 'var(--spacing-md)'
                            }}>
                                Authorization by IP
                            </h3>

                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--spacing-md)',
                                marginBottom: 'var(--spacing-md)'
                            }}>
                                <div>
                                    <p style={{
                                        color: 'var(--text-muted)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0 0 var(--spacing-xs) 0'
                                    }}>
                                        Socks5 Server:Port
                                    </p>
                                    <p style={{
                                        color: 'var(--text-light)',
                                        fontSize: 'var(--text-sm)',
                                        margin: '0',
                                        fontFamily: 'var(--font-mono)'
                                    }}>
                                        {selectedProxy?.socksAuth?.ip}:{selectedProxy?.ipAuth?.port}
                                    </p>
                                </div>
                                <div>
                                    <p style={{
                                        color: 'var(--primary-orange)',
                                        fontSize: 'var(--text-base)',
                                        fontWeight: '600',
                                        margin: '0 0 var(--spacing-xs) 0'
                                    }}>
                                        Whitelist (e.g. 1.1.1.1,1.1.1.2)
                                    </p>
                                    <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                                        <button
                                            type="button"
                                            className="btn btn-outline btn-small"
                                            disabled={isSettingMyIp}
                                            onClick={handleUseMyIp}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minWidth: '70px',
                                                flexShrink: 0,
                                                opacity: isSettingMyIp ? 0.7 : 1,
                                                cursor: isSettingMyIp ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            {isSettingMyIp ? <LoadingSpinner size={20} /> : 'My IP'}
                                        </button>
                                        <input
                                            type="text"
                                            value={whitelist ?? ''}
                                            onChange={handleWhiteListChange}
                                            placeholder="Enter IP addresses"
                                            style={{
                                                flex: 1,
                                                padding: 'var(--spacing-sm)',
                                                background: 'var(--dark-gray)',
                                                border: '1px solid var(--light-gray)',
                                                borderRadius: 'var(--radius-sm)',
                                                color: 'var(--text-light)',
                                                fontSize: 'var(--text-sm)',
                                                fontFamily: 'var(--font-mono)',
                                                minWidth: 0,
                                                maxWidth: '100%'
                                            }}
                                        />
                                        <button
                                            className="btn btn-outline btn-small"
                                            disabled={buttonStatus}
                                            onClick={handleWhitelistSave}
                                            style={{ minWidth: '60px', flexShrink: 0 }}
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Connection String */}
                        <div style={{
                            background: 'var(--dark-gray)',
                            border: '1px solid var(--light-gray)',
                            borderRadius: 'var(--radius-md)',
                            padding: 'var(--spacing-md)',
                            marginBottom: 'var(--spacing-lg)'
                        }}>
                            <h3 style={{
                                color: 'var(--text-light)',
                                fontSize: 'var(--text-base)',
                                fontWeight: '600',
                                marginBottom: 'var(--spacing-sm)'
                            }}>
                                Connection String
                            </h3>
                            {/* Переключатель форматов */}
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 'var(--spacing-xs)',
                                marginBottom: 'var(--spacing-sm)'
                            }}>
                                {connFormats.map((fmt) => (
                                    <button
                                        key={fmt.id}
                                        type="button"
                                        onClick={() => setConnFormat(fmt.id)}
                                        style={{
                                            padding: '4px 10px',
                                            borderRadius: 'var(--radius-sm)',
                                            border: `1px solid ${connFormat === fmt.id ? 'var(--primary-orange)' : 'var(--light-gray)'}`,
                                            background: 'transparent',
                                            color: connFormat === fmt.id ? 'var(--primary-orange)' : 'var(--text-muted)',
                                            fontSize: 'var(--text-xs)',
                                            fontWeight: connFormat === fmt.id ? '600' : '500',
                                            cursor: 'pointer',
                                            transition: 'var(--transition-normal)'
                                        }}
                                    >
                                        {fmt.label}
                                    </button>
                                ))}
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-sm)'
                            }}>
                                <button
                                    type="button"
                                    onClick={handleCopyConnectionString}
                                    title={connCopied ? 'Copied!' : 'Copy'}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0',
                                        background: 'transparent',
                                        border: 'none',
                                        color: connCopied ? 'var(--primary-orange)' : 'var(--text-muted)',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        transition: 'var(--transition-normal)'
                                    }}
                                >
                                    {connCopied
                                        ? <CheckIcon style={{ fontSize: '14px' }} />
                                        : <ContentCopyOutlinedIcon style={{ fontSize: '14px' }} />}
                                </button>
                                <p style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-xs)',
                                    margin: '0',
                                    fontFamily: 'var(--font-mono)',
                                    wordBreak: 'break-all'
                                }}>
                                    {buildConnectionString()}
                                </p>
                            </div>
                        </div>



                        {/* Кнопки действий */}
                        <div style={{
                            display: 'flex',
                            gap: 'var(--spacing-md)',
                            justifyContent: 'center',
                            flexDirection: isMobile ? 'column' : 'row'
                        }}>
                            {/* Кнопка проверки скорости - показываем только на десктопе */}
                            {!isMobile && (
                                <button
                                    onClick={checkSpeed}
                                    className="btn btn-outline"
                                    style={{ 
                                        minWidth: '120px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        opacity: (isCheckingSpeed || isLoadingSpeed) ? 0.7 : 1,
                                        cursor: (isCheckingSpeed || isLoadingSpeed) ? 'not-allowed' : 'pointer'
                                    }}
                                    disabled={isCheckingSpeed || isLoadingSpeed}
                                >
                                    {isCheckingSpeed ? (
                                        <LoadingSpinner size={22} />
                                    ) : isLoadingSpeed ? (
                                        <LoadingSpinner />
                                    ) : (
                                        <Tooltip 
                                            title={speedCheckTime ? moment(speedCheckTime).fromNow() : 'Speed test result'}
                                        >
                                            <span>
                                                {proxySpeed === 'Error' ? 'Error' : proxySpeed != null ? `${proxySpeed} Mbps` : 'Check speed'}
                                            </span>
                                        </Tooltip>
                                    )}
                                </button>
                            )}
                            
                            {/* На мобильных устройствах показываем только результат скорости */}
                            {isMobile && (
                                <button
                                    className="btn btn-outline"
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        opacity: (isCheckingSpeed || isLoadingSpeed) ? 0.7 : 1,
                                        cursor: (isCheckingSpeed || isLoadingSpeed) ? 'not-allowed' : 'pointer'
                                    }}
                                    disabled={isCheckingSpeed || isLoadingSpeed}
                                >
                                    <Tooltip 
                                        title={speedCheckTime ? moment(speedCheckTime).fromNow() : 'Hold to test speed'}
                                        onLongPress={() => {
                                            if (!isCheckingSpeed && !isLoadingSpeed) {
                                                checkSpeed()
                                            }
                                        }}
                                        isCheckingSpeed={isCheckingSpeed}
                                    >
                                        <span>
                                            {isLoadingSpeed ? (
                                                <LoadingSpinner />
                                            ) : isCheckingSpeed ? (
                                                <LoadingSpinner size={22} />
                                            ) : (
                                                proxySpeed === 'Error' ? 'Error' : proxySpeed != null ? `${proxySpeed} Mbps` : 'Check speed'
                                            )}
                                        </span>
                                    </Tooltip>
                                </button>
                            )}
                            <button
                                className={`btn ${canRefund ? 'btn-buy' : 'btn-outline'} btn-small`}
                                onClick={handleRefundClick}
                                disabled={!canRefund}
                                style={{ 
                                    width: isMobile ? '100%' : undefined,
                                    minWidth: isMobile ? undefined : '100px',
                                    height: isMobile ? '40px' : undefined,
                                    opacity: canRefund ? 1 : 0.5
                                }}
                            >
                                Refund
                            </button>
                            <button
                                className="btn btn-buy btn-small"
                                onClick={handleRenewClick}
                                style={{
                                    width: isMobile ? '100%' : undefined,
                                    minWidth: isMobile ? undefined : '100px',
                                    height: isMobile ? '40px' : undefined
                                }}
                            >
                                Renew
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {/* Диалоги подтверждения */}
            <RenewProxyConfirmDialog 
                openConfirmRenew={openConfirmRenew} 
                setOpenConfirmRenew={setOpenConfirmRenew}
                renewProxy={renewProxy}
                selectedProxy={selectedProxy}
            />

            <RefundProxyConfirmDialog 
                openConfirmRefund={openConfirmRefund}
                refundProxy={refundProxy}
                setOpenConfirmRefund={setOpenConfirmRefund}
                selectedProxy={selectedProxy}
            />

            {/* Уведомления */}
            <CustomSnackbar
                open={saveWhitelistError}
                onClose={handleSetSaveErrorClose}
                message="Error saving whitelist."
                severity="error"
            />

            <CustomSnackbar
                open={openSuccessWhitelistUpdate}
                onClose={handleSetOpenSuccessWhitelistUpdate}
                message="Proxy whitelist updated!"
                severity="success"
            />

            <CustomSnackbar
                open={renewError}
                onClose={handleSetRenewErrorClose}
                message="Error renew proxy."
                severity="error"
            />

            <CustomSnackbar
                open={renewSuccess}
                onClose={handleSetRenewSuccessClose}
                message="Proxy renewed successfully!"
                severity="success"
            />

            <CustomSnackbar
                open={refundError}
                onClose={handleSetRefundErrorClose}
                message="Error creating refund ticket."
                severity="error"
            />

            <CustomSnackbar
                open={refundSuccess}
                onClose={handleSetRefundSuccessClose}
                message="Refund ticket created successfully!"
                severity="success"
            />

            <CustomSnackbar
                open={duplicateTicketError}
                onClose={handleSetDuplicateTicketErrorClose}
                message="A refund ticket for this proxy already exists!"
                severity="error"
            />
        </>
    )
}

export default ProxyDetailsDialog